import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// For someone stuck mid-registration whose bescom_reg_sid cookie is gone
// (closed an incognito window, switched device/browser, cleared cookies —
// registration.js's whole "survive a refresh" mechanism depends on that
// cookie, and losing it left no way back in). Finds the account by
// whatever they can retype — username, email, or phone, since not
// everyone remembers which they used — then hands off to the existing
// claim-account flow (same one bulk onboarding uses) via the same
// generic claim-token mechanism. See resumeRegistration.js (backend).

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

const page = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  backgroundImage: 'url(/BESCOM_SSO_BG_Image.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

const card = {
  background: 'rgba(255, 255, 255, 0.65)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  borderRadius: '16px',
  padding: '48px 40px',
  width: '100%',
  maxWidth: '420px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
};

const lbl = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '500',
  color: '#5F5E5A',
  marginBottom: '5px',
};

const inp = {
  width: '100%',
  padding: '10px 14px',
  border: '1.5px solid #D3D1C7',
  borderRadius: '8px',
  fontSize: '15px',
  boxSizing: 'border-box',
  outline: 'none',
};

const btn = {
  width: '100%',
  padding: '14px',
  background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  marginTop: '8px',
};

const btnDisabled = { ...btn, opacity: 0.6, cursor: 'not-allowed' };

export default function ResumeRegistration() {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Set once /find succeeds — proof the account was matched, but NOT
  // proof it's actually this person's (see resumeRegistration.js's own
  // header for why a code is required before handing out a claim token).
  const [verify, setVerify] = useState(null); // { attemptId, channel, maskedDestination }
  const [code, setCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Shared by the initial submit and the resend button — a resend is
  // just asking /find again for the same identifier, which mints a fresh
  // attemptId/code and quietly invalidates whatever attempt was running
  // before (a stale attemptId just won't match a later verify-code call).
  async function requestCode() {
    if (!identifier.trim()) return false;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/resume-registration/find`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: identifier.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong.');
        return false;
      }
      setVerify(data);
      setCode('');
      return true;
    } catch (e) {
      setError('Could not reach the account service.');
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function handleFindSubmit(e) {
    e.preventDefault();
    await requestCode();
  }

  async function handleResend() {
    const ok = await requestCode();
    if (ok) setResendCooldown(30);
  }

  async function handleCodeSubmit(e) {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/resume-registration/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attemptId: verify.attemptId, code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong.');
        return;
      }
      navigate(`/claim-account?token=${encodeURIComponent(data.token)}`, { replace: true });
    } catch (e) {
      setError('Could not reach the account service.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={page}>
      <div style={card}>
        <button
          onClick={() => navigate('/')}
          style={{
            background: '#f8f9fa', border: '1px solid #dee2e6', color: '#495057',
            fontSize: '13px', cursor: 'pointer', padding: '8px 16px',
            borderRadius: '4px', marginBottom: '16px', display: 'flex',
            alignItems: 'center', gap: '6px',
          }}
        >
          ← Home
        </button>

        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/bescom-logo-ui.png" alt="BESCOM" style={{ width: '80px', marginBottom: '16px' }} />
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px' }}>
            Continue your registration
          </h1>
          <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
            {verify
              ? `Enter the code sent to ${verify.maskedDestination}.`
              : 'Enter your username, email, or phone number to pick up where you left off.'}
          </p>
        </div>

        {error && (
          <div style={{
            background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
            borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        {!verify ? (
          <form onSubmit={handleFindSubmit}>
            <label style={lbl}>Username, email, or phone number *</label>
            <input
              style={inp}
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoFocus
              required
            />
            <button type="submit" disabled={loading || !identifier.trim()} style={(loading || !identifier.trim()) ? btnDisabled : btn}>
              {loading ? 'Please wait…' : 'Continue'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleCodeSubmit}>
            <label style={lbl}>Verification code *</label>
            <input
              style={inp}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
              required
            />
            <button type="submit" disabled={loading || code.length !== 6} style={(loading || code.length !== 6) ? btnDisabled : btn}>
              {loading ? 'Please wait…' : 'Verify'}
            </button>
            <button
              type="button"
              onClick={handleResend}
              disabled={loading || resendCooldown > 0}
              style={{
                width: '100%', padding: '10px',
                background: 'none', border: '1px solid #D3D1C7', color: '#666',
                borderRadius: '10px', fontSize: '13px', marginTop: '10px',
                cursor: (loading || resendCooldown > 0) ? 'not-allowed' : 'pointer',
                opacity: (loading || resendCooldown > 0) ? 0.6 : 1,
              }}
            >
              {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Didn't get it? Resend code"}
            </button>
            <button
              type="button"
              onClick={() => { setVerify(null); setCode(''); setError(null); }}
              style={{
                width: '100%', padding: '10px', background: 'none', border: 'none',
                color: '#666', fontSize: '13px', cursor: 'pointer', marginTop: '10px',
                textDecoration: 'underline',
              }}
            >
              Use a different username, email, or phone number
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
